import React, { useCallback, useLayoutEffect, useState } from 'react'
import Box from '@material-ui/core/Box'
import BaseTabs from '@material-ui/core/Tabs'
import BaseTab from '@material-ui/core/Tab'
import Typography from '@material-ui/core/Typography'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import BaseListItemText from '@material-ui/core/ListItemText'
import withStyles from '@material-ui/core/styles/withStyles'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'

import Link from '../Link'
import InquiryWidget from '../InquiryWidget/InquiryWidget'

import usePressPageData from '../../hooks/usePressPageData'
import { NewsItem, PressConstants, PressItem } from '../../types/press'
import { isBrowser } from '../../utils/env'
import Button from '@material-ui/core/Button'
import { useTranslation } from 'react-i18next'

interface PressProps {
  sectionClass?: string
}

const { pressPageTabs, newsTab, pressTab, incrementValue } = PressConstants
const Press: React.FC<PressProps> = ({ sectionClass }) => {
  const [activeTab, setActiveTab] = React.useState(pressPageTabs[0])
  const loadIncrement = incrementValue
  const { t } = useTranslation()

  const [limit, setLimit] = useState({
    [newsTab]: loadIncrement,
    [pressTab]: loadIncrement,
  })

  const {
    newsItems: { nodes: newsItems },
    pressItems: { nodes: pressItems },
    contentfulPressPage: {
      inquiryWidget: { contactName, email },
    },
  } = usePressPageData()

  const loadMoreItems = useCallback(
    (tab: string) => {
      setLimit((prevState) => ({
        ...prevState,
        [tab]: Math.min(
          prevState[tab] + loadIncrement,
          tab === newsTab ? newsItems.length : pressItems.length
        ),
      }))
    },
    [newsItems, pressItems, loadIncrement]
  )

  useLayoutEffect(() => {
    // Pull the current active tab off of the session storage if available in the browser
    const setSessionTabState = (): void => {
      if (isBrowser) {
        const tab = sessionStorage.getItem('currentTab')
        if (tab) {
          return setActiveTab(tab)
        }
      }
    }
    setSessionTabState()
  }, [])
  const classes = useStyles()

  if (!activeTab) return null

  const handleChange = (
    _event: React.ChangeEvent<Record<string, unknown>>,
    newValue: string
  ) => {
    setActiveTab(newValue)
    // Set the current selected tab in the session storage as we wanna keep the user
    // in the same section once he/she returns to the landing press page after reading
    // a press article
    if (isBrowser) {
      sessionStorage.setItem('currentTab', newValue)
    }
  }

  return (
    <Box className={classes.pullContainerUp}>
      <Box className={classes.mdWidgetContainer}>
        <InquiryWidget
          contactName={contactName}
          email={email}
          className={classes.mdWidget}
          lowerSectionClass={classes.hideSection}
        />
      </Box>
      <Tabs value={activeTab} onChange={handleChange} className={sectionClass}>
        {pressPageTabs.map((tab) => (
          <Tab
            key={tab}
            label={
              <Typography className={classes.letterSpacing} variant="h5">
                {tab}
              </Typography>
            }
            value={tab}
          />
        ))}
      </Tabs>
      <Box className={clsx(classes.container, sectionClass)}>
        <Box className={clsx(classes.listContainer, classes.pullContentUp)}>
          <List className={classes.list}>
            {activeTab === newsTab ? (
              <>
                <NewsItems newsItems={newsItems} newsLimit={limit[newsTab]} />
                {limit[newsTab] < newsItems.length && (
                  <Box className={classes.buttonContainer}>
                    <Button
                      onClick={() => loadMoreItems(activeTab)}
                      variant="outlined"
                      className={classes.button}
                    >
                      {t('press.viewMore')}
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <>
                <PressItems
                  pressItems={pressItems}
                  pressLimit={limit[pressTab]}
                />
                {limit[pressTab] < pressItems.length && (
                  <Box className={classes.buttonContainer}>
                    <Button
                      onClick={() => loadMoreItems(activeTab)}
                      variant="outlined"
                      className={classes.button}
                    >
                      {t('press.viewMore')}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </List>
        </Box>
        <Box className={clsx(classes.pullContentUp, classes.lgWidgetContainer)}>
          <InquiryWidget contactName={contactName} email={email} />
        </Box>
      </Box>
    </Box>
  )
}

const NewsItems = ({
  newsItems,
  newsLimit,
}: {
  newsItems: NewsItem[]
  newsLimit: number
}) => {
  const classes = useStyles()

  return newsItems.slice(0, newsLimit).map((newsItem) => (
    <Link
      className={classes.itemLinkWrapper}
      key={newsItem.id}
      to={newsItem.url}
      target="_blank"
    >
      <ListItem divider disableGutters className={classes.zeroPaddingBlock}>
        <ListItemText
          primary={
            <Typography variant="h3" className={classes.itemContentPrimary}>
              {newsItem.title}
            </Typography>
          }
          secondary={
            <Typography
              variant="body1"
              className={classes.itemContentSecondary}
            >
              {`${newsItem.publishDate} — ${newsItem.source.name}`}
            </Typography>
          }
        />
      </ListItem>
    </Link>
  ))
}

const PressItems = ({
  pressItems,
  pressLimit,
}: {
  pressItems: PressItem[]
  pressLimit: number
}) => {
  const classes = useStyles()

  return pressItems.slice(0, pressLimit).map((pressItem) => (
    <Link
      className={classes.itemLinkWrapper}
      key={pressItem.id}
      to={`/press/press-${pressItem.slug}`}
    >
      <ListItem divider disableGutters className={classes.zeroPaddingBlock}>
        <ListItemText
          primary={
            <Typography variant="h3" className={classes.itemContentPrimary}>
              {pressItem.title}
            </Typography>
          }
          secondary={
            <Typography
              variant="body1"
              className={classes.itemContentSecondary}
            >
              {pressItem.publishedDate}
            </Typography>
          }
        />
      </ListItem>
    </Link>
  ))
}

const ListItemText = withStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  multiline: {
    marginBlock: '2.5rem',
  },
}))(BaseListItemText)

interface TabsProps {
  value: string
  onChange: (
    event: React.ChangeEvent<Record<string, unknown>>,
    newValue: string
  ) => void
  className: string | undefined
  children: React.ReactNode
}

const Tabs = withStyles((theme) => ({
  root: {
    paddingBlock: '4rem',
    backgroundColor: theme.palette.newColors.greyBackground,

    '@media (max-width: 1023px)': {
      padding: '1.5rem 6% 3rem',
    },

    '@media (max-width: 767px)': {
      padding: '1rem 6% 2rem',
    },
  },
  indicator: {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    bottom: 24,

    '& > div': {
      width: 0,
      height: 0,
      borderBottom: `24px solid ${theme.colors.white[100]}`,
      borderLeft: '16px solid transparent',
      borderRight: '16px solid transparent',
    },
  },
  flexContainer: {
    gap: '3.5rem',

    '@media (max-width: 767px)': {
      justifyContent: 'space-between',
      gap: 0,
    },
  },
}))((props: TabsProps) => (
  <BaseTabs {...props} TabIndicatorProps={{ children: <div></div> }} />
))

interface TabProps {
  label: React.ReactNode
  value: string
}

const Tab = withStyles((theme) => ({
  root: {
    color: theme.palette.text.primary,
    letterSpacing: '0.1em',
    padding: 0,
    justifyContent: 'flex-start',
    minWidth: 'fit-content',

    '&:hover': {
      color: '#000',
    },
    '&:focus': {
      outline: 0,
    },
  },
  wrapper: {
    width: 'fit-content',
    alignItems: 'flex-start',
    marginBottom: '2.5rem',
  },
  selected: {
    outline: 0,
  },
}))((props: TabProps) => <BaseTab disableRipple {...props} />)

const useStyles = makeStyles((theme) =>
  createStyles({
    zeroPaddingBlock: {
      paddingBlock: 0,
    },
    pullContainerUp: {
      marginTop: '-5.5rem',

      '@media (max-width: 1023px)': {
        marginTop: '-5rem',
      },
    },
    letterSpacing: {
      letterSpacing: '0.1em',
    },
    container: {
      paddingBlock: '4rem',
      display: 'grid',
      gridTemplateColumns: '7.5fr 2.5fr',
      gap: '2rem',

      '@media (max-width: 1023px)': {
        gridTemplateColumns: '1fr',
      },
    },
    pullContentUp: {
      marginTop: '-8.2rem',

      '@media (max-width: 1023px)': {
        marginTop: '-7.2rem',
      },

      '@media (max-width: 767px)': {
        marginTop: '-6.2rem',
      },
    },
    listContainer: {
      backgroundColor: theme.palette.background.default,
    },
    list: {
      padding: '2.5rem',
    },
    itemLinkWrapper: {
      '&:focus': {
        outline: 0,
      },

      '&:first-child > li > div': {
        marginTop: 0,
      },

      '&:last-child > li': {
        borderBottom: 'none',
      },
    },
    itemContentPrimary: {
      color: theme.colors.blue[600],
    },
    itemContentSecondary: {
      color: theme.colors.gray[500],
    },
    lgWidgetContainer: {
      '@media (max-width: 1023px)': {
        display: 'none',
      },
    },
    mdWidgetContainer: {
      padding: '3rem 6% 1.5rem',
      backgroundColor: theme.palette.newColors.greyBackground,

      '@media (min-width: 1024px)': {
        display: 'none',
      },

      '@media (max-width: 767px)': {
        padding: '2rem 18% 1rem',
      },
    },
    mdWidget: {
      '@media (min-width: 768px)': {
        maxWidth: '40%',
      },
    },
    hideSection: {
      display: 'none',
    },
    button: {
      '&:focus': {
        outline: 'unset',
      },
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      margin: '3rem 0 0 0',
    },
  })
)

export default Press
